import { Profile } from '@/models/Profile';
import { ProfilePdfAnalysisResponse } from '@/views/profile/models/ProfileExtraction';
import normalizeExperiencesText from '@/views/profile/utils/normalizeExperiencesText';

/**
 * Converts the AI extraction response into the Partial<Profile> shape that
 * useProfilePreview consumes, and overlays the uploaded PDF as the
 * experiences document (the Profildatei).
 *
 * The backend's response already carries the IDs we need (skillTagId,
 * skillRoleTagId, countryIsoCode), so there are no name-only fallbacks
 * left. basic.country here holds the ISO code — saveBasic in previewSave
 * sends it through as countryIsoCode on accept. References and project
 * preferences aren't extracted, so they stay absent from the preview.
 */
const hasValue = (value?: string | null) => (
  value !== undefined
  && value !== null
  && value.trim() !== ''
);

const normalizeOptionalText = (value?: string | null) => {
  const normalizedValue = value?.trim();
  if (!normalizedValue) return undefined;
  return normalizedValue;
};

const normalizeOptionalYear = (value?: number | null) => {
  if (!value || value < 1950 || value > 2040) return undefined;
  return value;
};

const mapExtractionToPreview = (
  extracted: ProfilePdfAnalysisResponse,
  fileName: string,
  fileContents: string,
): Partial<Profile> => {
  const preview: Partial<Profile> = {};
  const skills = extracted.skills ?? [];
  const educations = extracted.educations ?? [];
  const certificates = extracted.certificates ?? [];

  if (
    hasValue(extracted.basic?.city)
    || hasValue(extracted.basic?.country)
    || hasValue(extracted.basic?.countryIsoCode)
    || hasValue(extracted.basic?.profileSummary)
  ) {
    const normalizedCountryIsoCode = normalizeOptionalText(extracted.basic?.countryIsoCode);
    preview.basic = {
      city: normalizeOptionalText(extracted.basic?.city),
      country: normalizedCountryIsoCode ?? normalizeOptionalText(extracted.basic?.country),
      profileSummary: normalizeOptionalText(extracted.basic?.profileSummary),
    };
  }

  if (skills.length) {
    preview.skills = {
      skillList: skills.map((s) => ({
        skillTagId: s.skillTagId,
        skillTagName: s.skillTagName,
        skillRoleTagId: s.skillRoleTagId,
        skillRoleTagName: s.skillRoleTagName,
      })),
    };
  }

  if (educations.length) {
    preview.educations = {
      educationsList: educations.map((edu) => ({
        label: edu.label ?? '',
        institute: edu.institute ?? '',
        year: normalizeOptionalYear(edu.year),
        link: edu.link ?? '',
        externalIdObject: '',
      })),
    };
  }

  if (certificates.length) {
    preview.certificates = {
      certificatesList: certificates.map((cert) => ({
        label: cert.label ?? '',
        issuer: cert.issuer ?? '',
        year: normalizeOptionalYear(cert.year),
        link: cert.link ?? '',
        externalIdObject: '',
      })),
    };
  }

  if (hasValue(extracted.experiences?.experiencesText) || (hasValue(fileName) && hasValue(fileContents))) {
    preview.experiences = {
      experiencesText: normalizeExperiencesText(extracted.experiences?.experiencesText ?? ''),
      experiencesDocumentLabel: fileName,
      experiencesDocument: fileContents,
    };
  }

  return preview;
};

export default mapExtractionToPreview;
